import { toNullableNumber, toNullableText } from '@/lib/helpers/numeric'
import useSQLite, { persistWebStore, readTableColumns, tableExists } from '../useSQLite'
import { defineSqliteFeature } from './define-feature'

const POLAR_CAPTURE_TTL_SEC = 90 * 86400
const DEFAULT_STATUS = 'imported'
const POLAR_CAPTURE_STATUS_UPLOADED = 'uploaded'
const LEGACY_STATUS_LINKED_TO_SESSION = 'linked_to_session'
export const POLAR_CAPTURE_SOURCE_MEMORY = 'polar_memory'
export const POLAR_CAPTURE_SOURCE_LIVE_BLE = 'polar_live_ble'
const POLAR_CAPTURE_SAME_RIDE_WINDOW_MS = 3 * 60 * 1000
const NOOP = async () => {}
const { dbRun, dbQuery, dbExecute } = useSQLite()
const POLAR_ABSOLUTE_TIMESTAMP_MS_THRESHOLD = 1_000_000_000_000

const SCHEMA_SQL = `
CREATE TABLE IF NOT EXISTS polar_captures (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    capture_key TEXT NOT NULL UNIQUE,
    source TEXT NOT NULL DEFAULT 'polar_memory',
    device_id TEXT,
    device_name TEXT,
    polar_exercise_ts INTEGER NOT NULL,
    started_at INTEGER,
    ended_at INTEGER,
    recording_intent_id TEXT,
    user_id INTEGER,
    sample_count INTEGER NOT NULL DEFAULT 0,
    duration_sec INTEGER NOT NULL DEFAULT 0,
    status TEXT NOT NULL DEFAULT 'imported',
    linked_session_id INTEGER,
    deleted_from_polar_at INTEGER,
    samples_json TEXT NOT NULL,
    created_at INTEGER NOT NULL,
    updated_at INTEGER NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_polar_captures_created_at ON polar_captures(created_at DESC);
CREATE INDEX IF NOT EXISTS idx_polar_captures_updated_at ON polar_captures(updated_at DESC);
CREATE INDEX IF NOT EXISTS idx_polar_captures_status ON polar_captures(status);
CREATE INDEX IF NOT EXISTS idx_polar_captures_source ON polar_captures(source);
`

const REQUIRED_COLUMNS = [
    {
        name: 'source',
        sql: `ALTER TABLE polar_captures ADD COLUMN source TEXT NOT NULL DEFAULT '${POLAR_CAPTURE_SOURCE_MEMORY}';`,
    },
    {
        name: 'started_at',
        sql: 'ALTER TABLE polar_captures ADD COLUMN started_at INTEGER;',
    },
    {
        name: 'ended_at',
        sql: 'ALTER TABLE polar_captures ADD COLUMN ended_at INTEGER;',
    },
    {
        name: 'recording_intent_id',
        sql: 'ALTER TABLE polar_captures ADD COLUMN recording_intent_id TEXT;',
    },
    // P7: the account the capture was made under, the only one that sends it.
    {
        name: 'user_id',
        sql: 'ALTER TABLE polar_captures ADD COLUMN user_id INTEGER;',
    },
]

let schemaReady = false
let schemaPromise = null

export const normalizePolarTimestampMs = (value) => {
    const number = toNullableNumber(value)
    if (!number || number <= 0) return null

    return Math.round(
        number < POLAR_ABSOLUTE_TIMESTAMP_MS_THRESHOLD
            ? number * 1000
            : number
    )
}

const normalizePolarCaptureSource = (source) => {
    const normalized = toNullableText(source)
    if (
        normalized !== POLAR_CAPTURE_SOURCE_MEMORY &&
        normalized !== POLAR_CAPTURE_SOURCE_LIVE_BLE
    ) {
        return POLAR_CAPTURE_SOURCE_MEMORY
    }
    return normalized
}

// The valid [time, bpm] pairs, as new arrays: also how the live recording copies its heart-rate streams.
export const normalizeSamples = (samples) =>
    Array.isArray(samples)
        ? samples
              .map((sample) => {
                  if (!Array.isArray(sample) || sample.length < 2) return null
                  const time = Number(sample[0])
                  const bpm = Number(sample[1])
                  if (!Number.isFinite(time) || !Number.isFinite(bpm)) return null
                  return [time, bpm]
              })
              .filter(Boolean)
        : []

const parseSamples = (value) => {
    if (!value || typeof value !== 'string') return []
    try {
        return normalizeSamples(JSON.parse(value))
    } catch {
        return []
    }
}

export const getPolarCaptureSourcePriority = (source) =>
    normalizePolarCaptureSource(source) === POLAR_CAPTURE_SOURCE_MEMORY ? 2 : 1

export const getPolarCaptureTimeBounds = (capture) => {
    if (!capture) return { startedAt: null, endedAt: null }

    const endedAt =
        normalizePolarTimestampMs(capture?.endedAt) ??
        normalizePolarTimestampMs(capture?.polarExerciseTs) ??
        null

    let startedAt = normalizePolarTimestampMs(capture?.startedAt)
    if (!startedAt && endedAt) {
        const durationMs = Math.max(0, Number(capture?.durationSec || 0) * 1000)
        startedAt = durationMs > 0 ? endedAt - durationMs : endedAt
    }

    return {
        startedAt: startedAt || null,
        endedAt: endedAt || null,
    }
}

const mapPolarCaptureRow = (row, { withSamples = false } = {}) => {
    if (!row) return null

    const capture = {
        id: Number(row.id),
        captureKey: row.capture_key,
        source: normalizePolarCaptureSource(row.source),
        deviceId: toNullableText(row.device_id),
        deviceName: toNullableText(row.device_name),
        polarExerciseTs: normalizePolarTimestampMs(row.polar_exercise_ts),
        startedAt: normalizePolarTimestampMs(row.started_at),
        endedAt: normalizePolarTimestampMs(row.ended_at),
        recordingIntentId: toNullableText(row.recording_intent_id),
        userId: toNullableNumber(row.user_id),
        sampleCount: Number(row.sample_count) || 0,
        durationSec: Number(row.duration_sec) || 0,
        status: toNullableText(row.status) || DEFAULT_STATUS,
        linkedSessionId: toNullableNumber(row.linked_session_id),
        deletedFromPolarAt: normalizePolarTimestampMs(row.deleted_from_polar_at) ?? toNullableNumber(row.deleted_from_polar_at),
        createdAt: normalizePolarTimestampMs(row.created_at) ?? (Number(row.created_at) || null),
        updatedAt: normalizePolarTimestampMs(row.updated_at) ?? (Number(row.updated_at) || null),
    }

    if (withSamples) {
        capture.samples = parseSamples(row.samples_json)
    }

    return capture
}

const ensurePolarCaptureColumns = async () => {
    const existingColumns = await readTableColumns('polar_captures')
    for (const column of REQUIRED_COLUMNS) {
        if (existingColumns.has(column.name)) continue
        await dbExecute(column.sql)
    }
}

const readCaptureByWhereClause = async (whereClause, params = [], { withSamples = false } = {}) => {
    await ensurePolarCaptureSchema()
    const result = await dbQuery(
        `SELECT
            id,
            capture_key,
            source,
            device_id,
            device_name,
            polar_exercise_ts,
            started_at,
            ended_at,
            recording_intent_id,
            user_id,
            sample_count,
            duration_sec,
            status,
            linked_session_id,
            deleted_from_polar_at,
            samples_json,
            created_at,
            updated_at
         FROM polar_captures
         WHERE ${whereClause}
         LIMIT 1;`,
        params
    )

    return mapPolarCaptureRow(result?.values?.[0], { withSamples })
}

export const ensurePolarCaptureSchema = async () => {
    if (schemaReady) return
    if (schemaPromise) return schemaPromise

    schemaPromise = dbExecute(SCHEMA_SQL)
        .then(() => ensurePolarCaptureColumns())
        .then(() => {
            schemaReady = true
        })
        .finally(() => {
            schemaPromise = null
        })

    return schemaPromise
}

export const buildPolarCaptureKey = ({
    source = POLAR_CAPTURE_SOURCE_MEMORY,
    deviceId = null,
    polarExerciseTs = null,
    referenceTs = null,
} = {}) => {
    const timestamp = normalizePolarTimestampMs(referenceTs ?? polarExerciseTs)
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
        throw new Error('A valid timestamp is required to build a Polar capture key')
    }

    const normalizedDeviceId = toNullableText(deviceId) || 'unknown-device'
    const normalizedSource = normalizePolarCaptureSource(source)
    return `${normalizedSource}:${normalizedDeviceId}:${timestamp}`
}

export const getPolarCaptureById = async (id, { withSamples = true } = {}) => {
    const captureId = Number(id)
    if (!Number.isFinite(captureId) || captureId <= 0) return null
    return readCaptureByWhereClause('id = ?', [captureId], { withSamples })
}

export const getPolarCaptureByKey = async (captureKey, { withSamples = false } = {}) => {
    const normalizedCaptureKey = toNullableText(captureKey)
    if (!normalizedCaptureKey) return null
    return readCaptureByWhereClause('capture_key = ?', [normalizedCaptureKey], { withSamples })
}

export const upsertPolarCapture = async ({
    captureKey,
    source = POLAR_CAPTURE_SOURCE_MEMORY,
    deviceId = null,
    deviceName = null,
    polarExerciseTs,
    startedAt = null,
    endedAt = null,
    recordingIntentId = undefined,
    userId = null,
    samples = [],
    status = undefined,
    linkedSessionId = undefined,
    deletedFromPolarAt = undefined,
} = {}) => {
    await ensurePolarCaptureSchema()

    const normalizedCaptureKey = toNullableText(captureKey)
    const normalizedSource = normalizePolarCaptureSource(source)
    const exerciseTs = normalizePolarTimestampMs(polarExerciseTs)
    const normalizedStartedAt = normalizePolarTimestampMs(startedAt)
    const normalizedEndedAt = normalizePolarTimestampMs(endedAt)
    const normalizedSamples = normalizeSamples(samples)

    if (!normalizedCaptureKey) {
        throw new Error('captureKey is required to save a Polar capture')
    }
    if (!Number.isFinite(exerciseTs) || exerciseTs <= 0) {
        throw new Error('polarExerciseTs is required to save a Polar capture')
    }
    if (!normalizedSamples.length) {
        throw new Error('samples are required to save a Polar capture')
    }

    const existingCapture = await getPolarCaptureByKey(normalizedCaptureKey)
    const now = Date.now()
    const nextStatus = toNullableText(status) || existingCapture?.status || DEFAULT_STATUS
    const nextLinkedSessionId =
        linkedSessionId === undefined
            ? toNullableNumber(existingCapture?.linkedSessionId)
            : toNullableNumber(linkedSessionId)
    const nextDeletedFromPolarAt =
        deletedFromPolarAt === undefined
            ? toNullableNumber(existingCapture?.deletedFromPolarAt)
            : toNullableNumber(deletedFromPolarAt)
    const nextRecordingIntentId =
        recordingIntentId === undefined
            ? toNullableText(existingCapture?.recordingIntentId)
            : toNullableText(recordingIntentId)
    // A capture stays with the account it was first made under.
    const nextUserId = existingCapture?.userId ?? toNullableNumber(userId)

    await dbRun(
        `INSERT INTO polar_captures (
            capture_key,
            source,
            device_id,
            device_name,
            polar_exercise_ts,
            started_at,
            ended_at,
            recording_intent_id,
            user_id,
            sample_count,
            duration_sec,
            status,
            linked_session_id,
            deleted_from_polar_at,
            samples_json,
            created_at,
            updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        ON CONFLICT(capture_key) DO UPDATE SET
            source = excluded.source,
            device_id = excluded.device_id,
            device_name = excluded.device_name,
            polar_exercise_ts = excluded.polar_exercise_ts,
            started_at = excluded.started_at,
            ended_at = excluded.ended_at,
            recording_intent_id = excluded.recording_intent_id,
            user_id = excluded.user_id,
            sample_count = excluded.sample_count,
            duration_sec = excluded.duration_sec,
            status = excluded.status,
            linked_session_id = excluded.linked_session_id,
            deleted_from_polar_at = excluded.deleted_from_polar_at,
            samples_json = excluded.samples_json,
            updated_at = excluded.updated_at;`,
        [
            normalizedCaptureKey,
            normalizedSource,
            toNullableText(deviceId),
            toNullableText(deviceName),
            exerciseTs,
            normalizedStartedAt,
            normalizedEndedAt,
            nextRecordingIntentId,
            nextUserId,
            normalizedSamples.length,
            Math.max(0, Number(normalizedSamples[normalizedSamples.length - 1]?.[0]) || 0),
            nextStatus,
            nextLinkedSessionId,
            nextDeletedFromPolarAt,
            JSON.stringify(normalizedSamples),
            existingCapture?.createdAt || now,
            now,
        ]
    )

    const capture = await getPolarCaptureByKey(normalizedCaptureKey, { withSamples: false })
    await persistWebStore()
    return {
        capture,
        created: !existingCapture,
    }
}

export const savePolarMemoryCapture = async ({
    deviceId = null,
    deviceName = null,
    polarExerciseTs,
    startedAt = null,
    endedAt = null,
    recordingIntentId = undefined,
    userId = null,
    samples = [],
    deletedFromPolarAt = undefined,
} = {}) => {
    const captureKey = buildPolarCaptureKey({
        source: POLAR_CAPTURE_SOURCE_MEMORY,
        deviceId,
        polarExerciseTs,
    })
    return upsertPolarCapture({
        captureKey,
        source: POLAR_CAPTURE_SOURCE_MEMORY,
        deviceId,
        deviceName,
        polarExerciseTs,
        startedAt,
        endedAt,
        recordingIntentId,
        userId,
        samples,
        deletedFromPolarAt,
    })
}

export const savePolarLiveBleCapture = async ({
    deviceId = null,
    deviceName = null,
    startedAt = null,
    endedAt = null,
    recordingIntentId = undefined,
    userId = null,
    samples = [],
} = {}) => {
    const startedTimestamp = normalizePolarTimestampMs(startedAt)
    const endedTimestamp = normalizePolarTimestampMs(endedAt)
    const referenceTs = endedTimestamp || startedTimestamp

    if (!referenceTs) {
        throw new Error('A valid startedAt or endedAt timestamp is required to save a live Polar capture')
    }

    const captureKey = buildPolarCaptureKey({
        source: POLAR_CAPTURE_SOURCE_LIVE_BLE,
        deviceId,
        referenceTs,
    })

    return upsertPolarCapture({
        captureKey,
        source: POLAR_CAPTURE_SOURCE_LIVE_BLE,
        deviceId,
        deviceName,
        polarExerciseTs: referenceTs,
        startedAt,
        endedAt,
        recordingIntentId,
        userId,
        samples,
    })
}

export const listPolarCaptures = async ({ limit = 20, withSamples = false } = {}) => {
    await ensurePolarCaptureSchema()

    const maxRows = Math.max(1, Number(limit) || 20)
    const samplesColumn = withSamples ? 'samples_json,' : ''
    const result = await dbQuery(
        `SELECT
            id,
            capture_key,
            source,
            device_id,
            device_name,
            polar_exercise_ts,
            started_at,
            ended_at,
            recording_intent_id,
            user_id,
            sample_count,
            duration_sec,
            status,
            linked_session_id,
            deleted_from_polar_at,
            ${samplesColumn}
            created_at,
            updated_at
         FROM polar_captures
         ORDER BY updated_at DESC
         LIMIT ?;`,
        [maxRows]
    )

    return (result?.values ?? []).map((row) => mapPolarCaptureRow(row, { withSamples }))
}

export const findRelatedPolarCapture = (capture, captures = []) => {
    if (!capture || !Array.isArray(captures) || captures.length === 0) return null

    const captureBounds = getPolarCaptureTimeBounds(capture)
    if (!captureBounds.startedAt && !captureBounds.endedAt) return null

    let bestCapture = null
    let bestScore = Number.POSITIVE_INFINITY

    for (const candidate of captures) {
        if (!candidate || candidate.id === capture.id) continue
        if (candidate.source === capture.source) continue
        if (candidate.deviceId && capture.deviceId && candidate.deviceId !== capture.deviceId) continue

        const candidateBounds = getPolarCaptureTimeBounds(candidate)
        if (!candidateBounds.startedAt && !candidateBounds.endedAt) continue

        const startDelta =
            captureBounds.startedAt && candidateBounds.startedAt
                ? Math.abs(captureBounds.startedAt - candidateBounds.startedAt)
                : Number.POSITIVE_INFINITY
        const endDelta =
            captureBounds.endedAt && candidateBounds.endedAt
                ? Math.abs(captureBounds.endedAt - candidateBounds.endedAt)
                : Number.POSITIVE_INFINITY
        const overlap =
            captureBounds.startedAt &&
            captureBounds.endedAt &&
            candidateBounds.startedAt &&
            candidateBounds.endedAt &&
            Math.max(captureBounds.startedAt, candidateBounds.startedAt) <=
                Math.min(captureBounds.endedAt, candidateBounds.endedAt)

        if (!overlap && startDelta > POLAR_CAPTURE_SAME_RIDE_WINDOW_MS && endDelta > POLAR_CAPTURE_SAME_RIDE_WINDOW_MS) {
            continue
        }

        const score = Math.min(startDelta, endDelta)
        if (score < bestScore) {
            bestCapture = candidate
            bestScore = score
        }
    }

    return bestCapture
}

export const markPolarCaptureDeletedFromPolar = async (id, deletedFromPolarAt = Date.now()) => {
    await ensurePolarCaptureSchema()

    const captureId = Number(id)
    if (!Number.isFinite(captureId) || captureId <= 0) {
        throw new Error('A valid capture id is required')
    }

    const deletedAt = Number(deletedFromPolarAt)
    const now = Date.now()
    await dbRun(
        `UPDATE polar_captures
         SET deleted_from_polar_at = ?, updated_at = ?
         WHERE id = ?;`,
        [Number.isFinite(deletedAt) ? deletedAt : now, now, captureId]
    )

    await persistWebStore()
    return getPolarCaptureById(captureId, { withSamples: false })
}

const updatePolarCaptureMetadata = async (id, { status = undefined, deletedFromPolarAt = undefined } = {}) => {
    await ensurePolarCaptureSchema()

    const captureId = Number(id)
    if (!Number.isFinite(captureId) || captureId <= 0) {
        throw new Error('A valid capture id is required')
    }

    const existingCapture = await getPolarCaptureById(captureId, { withSamples: false })
    if (!existingCapture) {
        throw new Error('Polar capture not found')
    }

    const nextStatus = toNullableText(status) || existingCapture.status || DEFAULT_STATUS
    const nextDeletedFromPolarAt =
        deletedFromPolarAt === undefined
            ? existingCapture.deletedFromPolarAt
            : toNullableNumber(deletedFromPolarAt)

    await dbRun(
        `UPDATE polar_captures
         SET status = ?,
             deleted_from_polar_at = ?,
             updated_at = ?
         WHERE id = ?;`,
        [nextStatus, nextDeletedFromPolarAt, Date.now(), captureId]
    )

    await persistWebStore()
    return getPolarCaptureById(captureId, { withSamples: false })
}

// Before P7, a capture went straight into its session (linked_to_session).
export const isPolarCaptureLinkedByFormerVersion = (capture) => capture?.status === LEGACY_STATUS_LINKED_TO_SESSION

// P7: a capture the server has, sent on its own since this version or uploaded
// into its session by a former one: never sent again, and safe to delete from
// the phone.
export const isPolarCaptureSent = (capture) =>
    capture?.status === POLAR_CAPTURE_STATUS_UPLOADED || isPolarCaptureLinkedByFormerVersion(capture)

export const markPolarCaptureUploaded = (id) =>
    updatePolarCaptureMetadata(id, { status: POLAR_CAPTURE_STATUS_UPLOADED })

export const removePolarCapture = async (id) => {
    await ensurePolarCaptureSchema()

    const captureId = Number(id)
    if (!Number.isFinite(captureId) || captureId <= 0) {
        throw new Error('A valid capture id is required')
    }

    await dbRun('DELETE FROM polar_captures WHERE id = ?;', [captureId])
    await persistWebStore()
    return { ok: true }
}

export const clearPolarCaptures = async () => {
    await ensurePolarCaptureSchema()
    const result = await dbRun('DELETE FROM polar_captures;')
    await persistWebStore()
    return {
        ok: true,
        cleared: result?.changes?.changes || 0,
    }
}

const getPolarCaptureSummary = async () => {
    const hasTable = await tableExists('polar_captures').catch(() => false)
    if (!hasTable) {
        return {
            capturesCount: 0,
            deletedFromPolarCount: 0,
            oldestCaptureAt: null,
            newestCaptureAt: null,
            now: Date.now(),
        }
    }

    const result = await dbQuery(
        `SELECT
            COUNT(*) as count,
            SUM(CASE WHEN deleted_from_polar_at IS NOT NULL THEN 1 ELSE 0 END) as deleted_count,
            MIN(created_at) as oldest,
            MAX(updated_at) as newest
         FROM polar_captures;`
    )
    const row = result?.values?.[0] || {}
    return {
        capturesCount: Number(row.count) || 0,
        deletedFromPolarCount: Number(row.deleted_count) || 0,
        oldestCaptureAt: Number(row.oldest) || null,
        newestCaptureAt: Number(row.newest) || null,
        now: Date.now(),
    }
}

const prunePolarCaptures = async ({ maxAgeSec } = {}) => {
    await ensurePolarCaptureSchema()

    const ttl = Number(maxAgeSec)
    if (!Number.isFinite(ttl) || ttl <= 0) {
        return { ok: false, cleared: 0 }
    }

    const cutoffMs = Date.now() - Math.floor(ttl * 1000)
    const result = await dbRun('DELETE FROM polar_captures WHERE updated_at < ?;', [cutoffMs])
    return {
        ok: true,
        cleared: result?.changes?.changes || 0,
    }
}

export default defineSqliteFeature({
    key: 'polarCaptures',
    ensureSchema: NOOP,
    onBoot: () => prunePolarCaptures({ maxAgeSec: POLAR_CAPTURE_TTL_SEC }),
    getSummary: () => getPolarCaptureSummary(),
    purge: () => clearPolarCaptures(),
    fallbacks: {
        onBoot: { ok: false, cleared: 0 },
        getSummary: null,
        purge: { ok: false, cleared: 0 },
    },
})
